/**
 * Benefits → Payroll Deductions (admin).
 *
 * Generates a per-employee, per-plan CSV with deduction amounts at
 * the org's chosen pay frequency. Same data drives the in-page
 * preview table.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Loader2 } from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import {
  getPayrollDeductions,
  payrollDeductionsCsvUrl,
} from "@/api/benefits";

type Frequency = "monthly" | "semi_monthly" | "biweekly" | "weekly";

export function PayrollDeductionsPage() {
  const [freq, setFreq] = useState<Frequency>("semi_monthly");
  const [rows, setRows] = useState<Record<string, string>[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPayrollDeductions(freq)
      .then((d) => !cancelled && setRows(d.rows))
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [freq]);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Payroll deductions"
        subtitle="Per-employee benefit deduction amounts ready for export to payroll"
      />

      <Card>
        <CardHeader>
          <CardTitle>Deductions</CardTitle>
          <div className="flex items-center gap-2">
            <Select
              value={freq}
              onChange={(e) => setFreq(e.target.value as Frequency)}
              options={[
                { value: "monthly", label: "Monthly (12/yr)" },
                { value: "semi_monthly", label: "Semi-monthly (24/yr)" },
                { value: "biweekly", label: "Bi-weekly (26/yr)" },
                { value: "weekly", label: "Weekly (52/yr)" },
              ]}
            />
            <a href={payrollDeductionsCsvUrl(freq)} download>
              <Button size="sm">
                <Download className="h-4 w-4" />
                Download CSV
              </Button>
            </a>
          </div>
        </CardHeader>

        {!rows ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            No active elections to export yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/40">
                <tr className="text-left text-xs uppercase text-text-muted">
                  <th className="py-2">Employee</th>
                  <th className="py-2">Plan</th>
                  <th className="py-2">Tier</th>
                  <th className="py-2 text-right">Per-period employee</th>
                  <th className="py-2 text-right">Per-period employer</th>
                  <th className="py-2 text-right">Monthly employee</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2 text-text-primary">
                      {r.first_name} {r.last_name}
                    </td>
                    <td className="py-2 text-text-primary truncate">
                      {r.plan_name}
                      <span className="text-text-muted text-xs ml-1">
                        ({r.plan_type})
                      </span>
                    </td>
                    <td className="py-2 text-text-muted">{r.tier}</td>
                    <td className="py-2 text-right tabular-nums text-text-primary">
                      ${
                        r[
                          `per_period_employee_cost (${freq})`
                        ] ?? "0.00"
                      }
                    </td>
                    <td className="py-2 text-right tabular-nums text-text-muted">
                      ${
                        r[
                          `per_period_employer_cost (${freq})`
                        ] ?? "0.00"
                      }
                    </td>
                    <td className="py-2 text-right tabular-nums text-text-muted">
                      ${r.monthly_employee_cost}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </motion.div>
  );
}

export default PayrollDeductionsPage;
